import { Injectable, inject } from "@angular/core";

import { CountriesApiService } from "@components/shared/countries/api";
import { ItemSupplyChainMapperService } from "@components/shared/items-supply-chain/item-supply-chain-mapper.service";
import { RiskAssessmentReportRecord } from "@components/shared/risk-assessment-reports/models";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { IItemExtended, IItemSupplyChain, ILocationExtended } from "@shared/interfaces";
import { AuthenticationService, DeliveriesService } from "@shared/services";
import { CommonUtils } from "@shared/utils";

type ReportRecord = RiskAssessmentReportRecord["record"];
type Params = { id: string; itemsIds?: string[] };

const { COUNTRY, DELIVERY, LOCATION, MATERIAL, ORGANISATION, PRODUCT } =
  RiskAssessmentTemplateResourceType;

@Injectable({
  providedIn: "root",
})
export class DeliveryRiskAssessmentReportRelatedRecordsService {
  private readonly authService = inject(AuthenticationService);

  private readonly deliveriesService = inject(DeliveriesService);

  private readonly countriesApi = inject(CountriesApiService);

  private readonly itemSupplyChainMapperService = inject(ItemSupplyChainMapperService);

  async get(params: Params): Promise<ReportRecord[]> {
    if (!this.authService.isRegularUser) {
      return null;
    }

    if (!params.itemsIds) {
      const { items } = await this.deliveriesService.get(params.id);

      params.itemsIds = items.map(({ item }) => CommonUtils.getUriId(item));
    }

    const supplyChainItems = await this.itemSupplyChainMapperService.mapItemSupplyChain(
      params.itemsIds,
    );

    const { deliveries, locations, materials, products } = this.processItems(supplyChainItems);
    const { countries, organisations } = await this.processLocations(locations);

    return [
      ...countries,
      ...organisations,
      ...materials,
      ...products,
      ...deliveries,
      ...locations.map(({ id, name }) => ({ id, name, type: LOCATION })),
    ];
  }

  private processItems(supplyChainItems: IItemSupplyChain[]) {
    const deliveries = new Map<string, ReportRecord>();
    const locations = new Map<string, ILocationExtended>();
    const materials = new Map<string, ReportRecord>();
    const products = new Map<string, ReportRecord>();

    const items: IItemExtended[] = Array.from(
      new Map(
        supplyChainItems
          .flatMap(({ deliveries }) => deliveries.flatMap(({ items }) => items))
          .map((item) => [item.id, item]),
      ).values(),
    );

    for (const { materials: itemMaterials, product } of items) {
      for (const { id, name } of itemMaterials) {
        materials.set(id, { id, name, type: MATERIAL });
      }

      if (product) {
        const { id, name } = product;

        products.set(id, { id, name, type: PRODUCT });
      }
    }

    for (const { locations: supplyLocations, deliveries: supplyDeliveries } of supplyChainItems) {
      for (const location of supplyLocations) {
        locations.set(location.id, location);
      }

      for (const { id, deliveryId } of supplyDeliveries) {
        deliveries.set(id, { id, name: deliveryId, type: DELIVERY });
      }
    }

    return {
      deliveries: Array.from(deliveries.values()),
      locations: Array.from(locations.values()),
      materials: Array.from(materials.values()),
      products: Array.from(products.values()),
    };
  }

  private async processLocations(locations: ILocationExtended[]) {
    // TODO: use a new API to get all Countries by codes
    const { content } = await this.countriesApi.getAll({});
    const countryMap = new Map(content.map((country) => [country.code, country]));

    const countries = new Map<string, ReportRecord>();
    const organisations = new Map<string, ReportRecord>();

    locations.map((location) => {
      const { address, organisationId } = location;

      if (!countries.has(address.country)) {
        const country = countryMap.get(address.country) ?? {
          id: null,
          name: address.countryName,
          code: address.country,
        };

        countries.set(address.country, { ...country, type: COUNTRY });
      }

      if (!organisations.has(organisationId)) {
        organisations.set(organisationId, {
          id: organisationId,
          name: location.organisationName,
          type: ORGANISATION,
        });
      }
    });

    return {
      countries: Array.from(countries.values()),
      organisations: Array.from(organisations.values()),
    };
  }
}
